import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { intersectionBy, isEmpty } from 'lodash';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip, Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import PopUpCustomFieldSelector from './PopUpCustomFieldSelector';
import CustomFieldEditor, { CUSTOM_FIELD_TYPES } from './CustomFieldEditor';
import api from '../../../../api';

const getDefaultValue = (type) => {
  switch (type) {
    case CUSTOM_FIELD_TYPES.INTEGER: return '0';
    case CUSTOM_FIELD_TYPES.LOCALIZED_TEXT: return {};
    case CUSTOM_FIELD_TYPES.BOOLEAN: return false;
    default: return null;
  }
};

const CustomFields = ({ currentProductData, currentCustomFields, setCurrentCustomFields }) => {
  const [parentCustomFields, setParentCustomFields] = useState({});
  const [fieldsOptions, setFieldsOptions] = useState([]);

  const parentCustomFieldsId = currentProductData?.customFieldsId?.parentValue;
  const canBeInherit = isEmpty(currentCustomFields) && !isEmpty(parentCustomFields);

  const handleApply = (selectedOptions) => {
    setCurrentCustomFields((prev) => selectedOptions
      .reduce((acc, { name, type }) => ({ ...acc, [name]: prev[name] || getDefaultValue(type) }), {}));
  };

  const handleInherit = () => setCurrentCustomFields({ ...parentCustomFields });

  const selectedCustomFields = useMemo(() => {
    const selectedOptions = Object.keys(currentCustomFields || {})
      .map((name) => ({ name }));

    return intersectionBy(fieldsOptions, selectedOptions, 'name');
  }, [currentCustomFields, fieldsOptions]);

  useEffect(() => {
    const customerId = currentProductData?.customerId;

    if (customerId) {
      api
        .getCustomFieldsDefinitionByCustomerId(customerId)
        .then(({ data: { items } = {} } = {}) => {
          if (!items) return;

          setFieldsOptions(items);
        });
    }
  }, [currentProductData?.customerId]);

  useEffect(() => {
    if (!parentCustomFieldsId) return;

    api.getCustomFieldById(parentCustomFieldsId)
      .then(({ data } = {}) => {
        if (!data) return;

        setParentCustomFields(data.customFields);
      });
  }, [parentCustomFieldsId]);

  return (
    <Box px={2}>
      <PopUpCustomFieldSelector
        selectedCustomFields={selectedCustomFields}
        onApply={handleApply}
        fieldsOptions={fieldsOptions}
      />
      {canBeInherit && (
      <Button
        variant="outlined"
        sx={{ marginBottom: 2 }}
        onClick={handleInherit}
      >
        Inherit from parent
      </Button>
      )}
      <Box>
        {!!selectedCustomFields?.length && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Value</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {selectedCustomFields.map(({ name, type, description }) => (
                  <TableRow key={name}>
                    <TableCell width="300px">
                      <Box p={2}>
                        <Typography>
                          {name}
                          {description && (
                            <Tooltip title={description}>
                              <HelpOutlineIcon fontSize="small" color="disabled" style={{ marginLeft: 4, verticalAlign: 'middle' }} />
                            </Tooltip>
                          )}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ padding: 2 }}>
                      <Box p={2}>
                        <CustomFieldEditor
                          name={name}
                          type={type}
                          value={currentCustomFields[name]}
                          setCurrentCustomFields={setCurrentCustomFields}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

CustomFields.propTypes = {
  currentProductData: PropTypes.object,
  currentCustomFields: PropTypes.object,
  setCurrentCustomFields: PropTypes.func,
};

export default CustomFields;
